import { useEffect, useRef } from "react";

/**
 * Behaviour of the shop "Vertical / business category" picker, shared by the admin
 * (pages/admin/_partials/ShopForm.jsx) and merchant (components/ShopFormFields.jsx) forms.
 */

/** Toggle one vertical. Picking a subcategory also selects its parent — unchanged, multi-select stays. */
export function toggleVerticalSelection(selected, id, { parentId = null } = {}) {
    const next = new Set(selected || []);
    if (next.has(id)) {
        next.delete(id);
    } else {
        next.add(id);
        if (parentId) next.add(parentId);
    }
    return [...next];
}

// A tap that arrives while the viewport is still resizing selects whatever moved under the finger.
// Reported Sep 2026 (Witty DoorStep, video): with the soft keyboard open the merchant tapped "Meat";
// that same tap dismissed the keyboard, the modal re-laid out, and "Food" — which landed on that spot —
// was selected and saved instead. Ignoring the ambiguous tap makes it dismiss the keyboard only; the
// next tap hits what the merchant actually sees.
export const VIEWPORT_SETTLE_MS = 400;
export const MIN_VIEWPORT_SHIFT_PX = 80;

/** Only real shifts count — ignore the few-pixel jitter of a scrolling address bar. */
export function isViewportShift(prevHeight, nextHeight) {
    return Math.abs((nextHeight || 0) - (prevHeight || 0)) >= MIN_VIEWPORT_SHIFT_PX;
}

/** Has the viewport been still long enough for a tap to mean what it looks like? */
export function isTapSettled(lastShiftAt, now = Date.now()) {
    return now - (lastShiftAt || 0) > VIEWPORT_SETTLE_MS;
}

/** Returns ``settled()`` — false while the viewport is still moving. No-op where visualViewport is absent. */
export function useViewportSettled() {
    const lastShiftAt = useRef(0);
    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        const vv = window.visualViewport || null;
        let height = vv ? vv.height : window.innerHeight;
        const onResize = () => {
            const next = vv ? vv.height : window.innerHeight;
            if (isViewportShift(height, next)) lastShiftAt.current = Date.now();
            height = next;
        };
        vv?.addEventListener("resize", onResize);
        window.addEventListener("resize", onResize);
        return () => {
            vv?.removeEventListener("resize", onResize);
            window.removeEventListener("resize", onResize);
        };
    }, []);
    return () => isTapSettled(lastShiftAt.current);
}
